import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/* ---------------------------------------------------------------------------
   How to use Admin, inside Admin.

   This is the source of truth for the Admin guide, not a copy of one. It ships
   with the code, so it always describes the version the sidebar names — a
   document kept anywhere else drifts the moment a screen changes, and nobody
   finds out until it has misled somebody.

   Scoring, review and incidents are the Facility Scorecard's, and its own guide
   covers them.
   --------------------------------------------------------------------------- */

const CONTENTS = [
  { id: 'what', label: 'What Admin is for' },
  { id: 'access', label: 'Signing in and roles' },
  { id: 'setup', label: 'First-time setup' },
  { id: 'coverage', label: 'How far the data reaches' },
  { id: 'import', label: 'Data Import' },
  { id: 'employees', label: 'Employees' },
  { id: 'schedules', label: 'Employee Schedules' },
  { id: 'facilities', label: 'Facilities' },
  { id: 'paycodes', label: 'Pay Codes' },
  { id: 'admin', label: 'Administration' },
  { id: 'audit', label: 'Audit Log' },
  { id: 'troubleshooting', label: 'When something looks wrong' },
];

export function Help() {
  const { hash } = useLocation();

  // Opens at the top. The ? arrives with no hash, and landing part-way down a
  // document you have just opened is disorienting even when the section is the
  // right one. The contents rail is how you get to a section.
  //
  // React Router does not scroll to a hash by itself, so the rail needs this
  // too — including when the hash is unchanged and only the click happened.
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <div className="help">
      <div className="page-header">
        <div>
          <div className="page-title">How to use Admin</div>
          <div className="page-subtitle">
            People, access, locations and data loading · this guide ships with the app, so it describes the version
            you are running
          </div>
        </div>
      </div>

      <div className="help__layout">
        <nav className="help__toc" aria-label="Contents">
          <div className="section-label" style={{ marginBottom: 10 }}>CONTENTS</div>
          {CONTENTS.map((c) => (
            <a key={c.id} href={`#${c.id}`} className="help__toc-link">{c.label}</a>
          ))}
        </nav>

        <div className="help__body">

          <Section id="what" title="What Admin is for">
            <p>
              Admin holds what every Cyntox app shares: who can sign in and what each person may do, the audit log,
              the list of locations, what each pay code means, and the loading of employees, schedules and punch
              data. The apps themselves — the Facility Scorecard and Inventory — read from it.
            </p>
            <p>
              Settings that belong to one app stay in that app. The Points Key, periods, scorecard scope and scoring
              rules are the Facility Scorecard's, under its Settings screen. The <i>Other apps</i> links in the
              sidebar take you there.
            </p>
          </Section>

          <Section id="access" title="Signing in and roles">
            <p>
              Sign in with your Cyntox Microsoft account. Signing in proves who you are — it grants nothing on its
              own. Until an administrator adds you on the Administration screen, you will be told your account has
              no access yet.
            </p>
            <p>
              Access is given <b>per app</b>: an app, a role in that app, and where it applies. Each role belongs to
              one app and can only open that app's screens, so someone who works in two apps has a line for each.
              For the Facility Scorecard and Admin, <i>where</i> is a location (or all locations); for Inventory it
              is a <b>region</b> — East, Heartland or Midwest (or all regions) — because counts are organised by
              region. Someone covering two plants, or two regions, gets two lines. The sidebar shows what you hold under{' '}
              <i>Signed in as</i>, the menu lists only screens your roles reach, and the <i>Other apps</i> links
              appear only when your roles reach something there.
            </p>
            <h3>Admin's roles</h3>
            <table className="table help__table">
              <thead>
                <tr>
                  <th>ROLE</th><th>WHAT IT REACHES IN ADMIN</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="strong">Admin</td><td>Everything in Admin: people and access, data import, employees, schedules, facilities, pay codes and the audit log</td></tr>
                <tr><td className="strong">Operations Admin</td><td>Data import, employees and schedules. Not access, facilities, pay codes or the audit log</td></tr>
                <tr><td className="strong">Data Import</td><td>Data import, employees and schedules — the same as Operations Admin today, kept as its own role for someone who only loads files</td></tr>
              </tbody>
            </table>
            <p className="meta">
              The Facility Scorecard has its own roles — Location Manager, Admin, Operations Admin, Executive and
              Team Member — and so does Inventory — Operations Admin, Driver Supervisor and Viewer — each described
              in that app's guide. The Roles tab on Administration shows exactly what every role can do.
            </p>
          </Section>

          <Section id="setup" title="First-time setup">
            <p>
              Admin's part of setting up comes first, because everything else refers to it. Then the Facility
              Scorecard's Settings: scorecard scope, Points Key, bands, scoring rules and periods — its own guide
              walks through those.
            </p>
            <ol className="help__steps">
              <li>
                <b>Facilities</b> — every location that will appear in a filter, a role assignment or an employee
                record. An employee import rejects a row whose location is not on this list.
              </li>
              <li>
                <b>Employees and schedules</b> — import the HR export and the weekly schedules.
              </li>
              <li>
                <b>Pay codes</b> — once punch data is loaded, classify every code it carries. See Pay Codes below.
              </li>
              <li>
                <b>People and access</b> — Administration. See below.
              </li>
            </ol>
          </Section>

          <Section id="coverage" title="How far the data reaches">
            <p>
              {/* Explains the bar; deliberately does not repeat what the bar
                  currently says. A guide that reports live status goes stale
                  the moment it is read, and the bar is right there anyway. */}
              The bar across the top of every screen says how far the imported punch data actually goes, and when
              each kind of file was last loaded. The per-kind dates are separate on purpose: loading punch data says
              nothing about when the employee master was last refreshed.
            </p>
            <p>
              The bar turns orange when the punch data is more than two days behind, or when the current period has
              nothing imported at all.
            </p>
          </Section>

          <Section id="import" title="Data Import">
            <p>
              Three kinds of file, one flow: drop it, read the preview, then commit. Nothing is saved until you
              commit. A file covers every location, so uploading needs Data Import at <b>all locations</b>; someone
              whose Data Import is limited to some locations sees the history only.
            </p>
            <table className="table help__table">
              <thead><tr><th>TAB</th><th>CONTAINS</th><th>EFFECT ON SCORING</th></tr></thead>
              <tbody>
                <tr><td className="strong">Punch Data</td><td>Clock in / out rows from the time system</td><td>Rebuilds attendance flags for the dates covered</td></tr>
                <tr><td className="strong">Employees</td><td>The employee master</td><td>Decides who is scored at all</td></tr>
                <tr><td className="strong">Schedules</td><td>Weekly start and end times</td><td>Decides what counts as late or absent</td></tr>
              </tbody>
            </table>
            <p>
              The preview groups problems by cause and says where the fix lives — an unknown employee is fixed in
              the employee master, an unrecognised location on Facilities.{' '}
              <b>Unmatched</b> punch rows are for an employee ID not in the master; they still import and start
              counting the moment that employee's record lands, which usually means a new hire whose record has not
              arrived yet.
            </p>
            <p>
              Committing applies the rows and rebuilds the Facility Scorecard's daily records for the affected
              dates. Punches replace by employee-day, so re-importing a corrected day overwrites that day for that
              person and leaves everyone else untouched. Import History records every file, who loaded it, and what
              it changed.
            </p>
          </Section>

          <Section id="employees" title="Employees">
            <p>
              The employee master. Search by name or ID, filter by department and location. You can add and edit
              people here without an import — a new hire who needs a record before their punches arrive, or someone
              who needs a record so they can be given access but will never appear in punch data.
            </p>
            <Callout label="Marking someone inactive" tone="warn">
              Do this when someone leaves. An active employee keeps generating daily records for every scheduled
              workday, so a departure left active accumulates absence flags indefinitely. Afterwards run{' '}
              <i>Rebuild daily records</i> on the Facility Scorecard's Daily Summary to clear the unreviewed flags
              they already generated.
            </Callout>
            <p>
              <b>Delete</b> is for a record entered by mistake. Someone with approved or rejected days, or anything in
              a locked period, cannot be deleted — their history is part of past scores — so mark them Terminated
              instead. <i>Show everyone, including terminated</i> lists people marked Terminated so they can be
              found and made active again.
            </p>
          </Section>

          <Section id="schedules" title="Employee Schedules">
            <p>
              Weekly start and end times per person — what late and absence are measured against. Leave a day blank
              to mean not scheduled. Bulk editing applies a pattern to several people at once.
            </p>
            <p>
              Changes apply going forward and do not alter past scoring by themselves, but a rebuild on the Facility
              Scorecard re-evaluates unreviewed days in the window, which is usually what you want after correcting
              a schedule.
            </p>
            <p>
              <b>Remove schedule</b> clears the unreviewed absence days that existed only because of it. Days already
              approved or rejected, and anything in a locked period, are kept.
            </p>
            <Callout label="No schedule is a legitimate state">
              <i>No schedule set</i> is a real, common, zero-point outcome, not an error to design away. Many
              Transportation employees genuinely have no fixed schedule, so lateness cannot be judged for them and
              their points come from the incident log only. Do not invent schedules to make the flag disappear.
            </Callout>
          </Section>

          <Section id="facilities" title="Facilities">
            <p>
              The locations every app shares. Role assignments, employee records and the location filters all refer
              to these codes. Add one before an HR export that mentions it, or the import rejects every employee at
              that location.
            </p>
            <Callout label="A code never changes" tone="warn">
              Employees and location-scoped users reference a facility by its code, so the code is fixed once
              created. Rename the facility's name freely; to stop using one, mark it inactive.
            </Callout>
          </Section>

          <Section id="paycodes" title="Pay Codes">
            <p>
              For each code in the time-clock data: does it <i>excuse the day</i>, and is a <i>missing punch
              expected</i> on it? Every app that reads punch data uses the same answers. Codes the data carries that
              have not been classified are listed first; until you classify one, days carrying it are held at zero
              points rather than guessed at. After classifying, run <i>Rebuild daily records</i> on the Facility
              Scorecard's Daily Summary so existing days are re-evaluated.
            </p>
            <Callout label="The most consequential tick in setup" tone="warn">
              An excused pay code means the day is never evaluated for lateness. Tick it only for genuine time away
              from work — Sick, Vacation, Holiday, Bereavement. <b>Overtime and Regular Pay are worked time.</b>{' '}
              One day often carries two codes, because the time system exports a row per code; a day counts as
              excused only when every row on it is excused, so a worked shift is still judged when an excused code
              sits beside it.
            </Callout>
          </Section>

          <Section id="admin" title="Administration">
            <p>
              Who can sign in, and what each person can see and do. Add someone by their Cyntox email address —
              that is the account they sign in with. Only company addresses are offered from the HR export.
            </p>
            <p>
              Access is given as <b>an app, a role in that app, and a location</b> (or all locations). Adding a
              person takes one line per app — for example <i>Facility Scorecard · Location Manager ·
              Newton-Heartland</i>. Every role belongs to one app, so the role list only offers roles for the app on
              that line, and a role can never open another app's screens.
            </p>
            <p>
              Someone covering two plants gets two lines, and their reach is the union — but permissions are per
              line, so a manager at Newton who also runs imports everywhere still approves at Newton only. The People
              list has a column per app showing each person's role and location there. To let someone see their own
              scorecard, link their login to their employee record.
            </p>
            <p>
              The <i>Roles</i> tab lists each app's roles and what each one can do, and can create new roles for an
              app. Every permission is listed in the words the screens use.
            </p>
            <Callout label="One permission to think twice about" tone="warn">
              Anyone with <b>Administration → add people and change access</b> can grant themselves anything,
              including full settings and scoring control. Keep that role to the few people who should have it.
            </Callout>
          </Section>

          <Section id="audit" title="Audit Log">
            <p>
              Every change in every app, newest first — who did it, when, and the record before and after.
              The screens that edit records each offer <i>History</i> on the record itself, which answers <i>how did
              this row get this way</i>. The Audit Log answers <i>what happened, and who did it</i>, across
              everything at once.
            </p>
            <p>
              For access changes it is the only place to look. Adding a person, granting a role, changing what a
              role can do: all recorded, none attached to a record screen with a history panel.
            </p>
            <ul className="help__list">
              <li>Filter by record type, action, person, date range or free text.</li>
              <li><b>Snapshot</b> on a row expands the stored before and after, side by side.</li>
              <li>Export the visible entries as CSV.</li>
            </ul>
          </Section>

          <Section id="troubleshooting" title="When something looks wrong">
            <table className="table help__table">
              <thead><tr><th>WHAT YOU SEE</th><th>WHAT IT MEANS</th></tr></thead>
              <tbody>
                <tr>
                  <td className="strong">An import rejects rows for a location</td>
                  <td>That location is not on Facilities yet. Add it, then preview the file again.</td>
                </tr>
                <tr>
                  <td className="strong">Unmatched punch rows</td>
                  <td>Punch data for an employee ID with no employee record. It is held, not lost — add or import that employee and it starts counting.</td>
                </tr>
                <tr>
                  <td className="strong">Pay Codes lists unclassified codes</td>
                  <td>Punch data carried codes nobody has classified yet. Those days score zero until you tick the boxes and rebuild daily records.</td>
                </tr>
                <tr>
                  <td className="strong">Someone signs in and sees “isn't set up yet”</td>
                  <td>They have no role. Add them on Administration with a role and a location.</td>
                </tr>
                <tr>
                  <td className="strong">A conflict banner on save</td>
                  <td>Someone else changed that record while you had it open. Nothing was overwritten. Reload and redo your change.</td>
                </tr>
                <tr>
                  <td className="strong">A screen is missing from the menu</td>
                  <td>Your roles do not reach it. The sidebar lists what you hold; an administrator can extend it.</td>
                </tr>
              </tbody>
            </table>
            <Callout label="Nothing is ever silently overwritten">
              Every change is recorded with who did it, when, and a full before-and-after snapshot. <i>History</i>{' '}
              appears on schedules and on the records the other apps edit. If something looks wrong, the history will
              say how it got that way.
            </Callout>
          </Section>

        </div>
      </div>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="help__section">
      <h2 className="help__h2">{title}</h2>
      {children}
    </section>
  );
}

function Callout({ label, tone, children }: { label: string; tone?: 'warn'; children: React.ReactNode }) {
  return (
    <div className={`help__callout${tone === 'warn' ? ' help__callout--warn' : ''}`}>
      <div className="help__callout-label">{label}</div>
      <div>{children}</div>
    </div>
  );
}
